import { FormEvent, useEffect, useState } from 'react';
import { Modal } from '../../../components/Modal';
import PriceInput from '../../../components/inputs/Price';
import { useAppContext } from '../../../contexts/AppContext';
import { SettingsRow, SettingsSection } from '../../configuracoes/estabelecimento/Form/SettingsGroup';
import { useEstoqueContext } from '../Context';
import { EstoqueService } from '../Service';
import { INVENTORY_UNIT_OPTIONS, INVENTORY_UNIT_VALUES } from '../units';

const service = new EstoqueService();

interface ItemFormProps {
    open: boolean;
    initialData?: any;
    onClose: () => void;
    onSaved: () => void | Promise<void>;
}

const emptyForm = {
    name: '',
    type: 'CONSUMIVEL',
    unit: '',
    brand: '',
    sku: '',
    minStock: '',
    reorderPointManual: '',
    leadTimeDays: '',
    costPrice: '',
    salePrice: '',
    isActive: true,
};

const toOptionalNumber = (value: unknown) => (value === '' || value === null ? undefined : Number(value));

export default function ItemForm({ open, initialData, onClose, onSaved }: ItemFormProps) {
    const { successSnackbar, errorSnackbar } = useAppContext();
    const { reloadSummary, reloadRecommendations } = useEstoqueContext();
    const [form, setForm] = useState<any>(emptyForm);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) {
            setForm({
                ...emptyForm,
                ...initialData,
                brand: initialData?.brand ?? '',
                sku: initialData?.sku ?? '',
                isActive: initialData?.isActive ?? true,
                minStock: initialData?.minStock ?? '',
                reorderPointManual: initialData?.reorderPointManual ?? '',
                leadTimeDays: initialData?.leadTimeDays ?? '',
                costPrice: initialData?.costPrice ?? '',
                salePrice: initialData?.salePrice ?? '',
                unit: initialData?.unit ? String(initialData.unit).trim().toUpperCase() : '',
            });
        }
    }, [open, initialData]);

    if (!open) return null;

    const handleChange = (field: string, value: any) => {
        setForm((prev: any) => ({ ...prev, [field]: value }));
    };

    const normalizedUnit = String(form.unit ?? '').trim().toUpperCase();
    const hasKnownUnit = INVENTORY_UNIT_VALUES.some((value) => value === normalizedUnit);
    const unitOptions = hasKnownUnit
        ? INVENTORY_UNIT_OPTIONS
        : normalizedUnit
          ? [{ value: normalizedUnit, label: `${normalizedUnit} (legado)` }, ...INVENTORY_UNIT_OPTIONS]
          : INVENTORY_UNIT_OPTIONS;

    const handleSubmit = async (event: FormEvent) => {
        event.preventDefault();

        const payload = {
            name: form.name.trim(),
            type: form.type,
            unit: normalizedUnit,
            brand: form.brand || undefined,
            sku: form.sku || undefined,
            minStock: toOptionalNumber(form.minStock),
            reorderPointManual: toOptionalNumber(form.reorderPointManual),
            leadTimeDays: toOptionalNumber(form.leadTimeDays),
            costPrice: toOptionalNumber(form.costPrice),
            salePrice: form.type === 'REVENDA' ? toOptionalNumber(form.salePrice) : undefined,
            isActive: !!form.isActive,
        };

        try {
            setSaving(true);

            if (initialData?._id) {
                await service.updateItem(initialData._id, payload);
                successSnackbar('Item atualizado com sucesso');
            } else {
                await service.createItem(payload);
                successSnackbar('Item criado com sucesso');
            }

            void reloadSummary();
            void reloadRecommendations();
            await onSaved();
            onClose();
        } catch (error: any) {
            console.error(error);
            errorSnackbar(error?.response?.data?.message || 'Erro ao salvar item');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal title={initialData ? 'Editar item' : 'Novo item'} onClose={onClose} desktopClassName="sm:!max-w-xl">
            <form className="estab-settings estab-settings--sheet space-y-6" onSubmit={handleSubmit}>
                <SettingsSection>
                    <SettingsRow label="Nome" htmlFor="item-nome">
                        <input
                            id="item-nome"
                            className="input estab-field"
                            value={form.name}
                            onChange={(e) => handleChange('name', e.target.value)}
                            required
                        />
                    </SettingsRow>
                    <SettingsRow label="Tipo" htmlFor="item-tipo">
                        <select
                            id="item-tipo"
                            className="select estab-field"
                            value={form.type}
                            onChange={(e) => handleChange('type', e.target.value)}
                        >
                            <option value="CONSUMIVEL">Consumível</option>
                            <option value="REVENDA">Revenda</option>
                        </select>
                    </SettingsRow>
                    <SettingsRow label="Unidade" htmlFor="item-unidade">
                        <select
                            id="item-unidade"
                            className="select estab-field"
                            value={form.unit}
                            onChange={(e) => handleChange('unit', e.target.value)}
                            required
                        >
                            <option value="">Selecione</option>
                            {unitOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </SettingsRow>
                    <SettingsRow label="Marca" htmlFor="item-marca">
                        <input
                            id="item-marca"
                            className="input estab-field"
                            value={form.brand}
                            placeholder="Opcional"
                            onChange={(e) => handleChange('brand', e.target.value)}
                        />
                    </SettingsRow>
                    <SettingsRow label="SKU" htmlFor="item-sku">
                        <input
                            id="item-sku"
                            className="input estab-field"
                            value={form.sku}
                            placeholder="Opcional"
                            onChange={(e) => handleChange('sku', e.target.value)}
                        />
                    </SettingsRow>
                </SettingsSection>

                <SettingsSection title="Preços">
                    <SettingsRow label="Custo">
                        <PriceInput
                            label=""
                            placeholder="R$ 0,00"
                            className="estab-field w-full"
                            value={form.costPrice}
                            onChange={(e) => handleChange('costPrice', e.target.value)}
                        />
                    </SettingsRow>
                    {form.type === 'REVENDA' && (
                        <SettingsRow label="Venda">
                            <PriceInput
                                label=""
                                placeholder="R$ 0,00"
                                className="estab-field w-full"
                                value={form.salePrice}
                                onChange={(e) => handleChange('salePrice', e.target.value)}
                                required
                            />
                        </SettingsRow>
                    )}
                </SettingsSection>

                <SettingsSection
                    title="Reposição"
                    footer="O item fica crítico quando o saldo chega ao mínimo ou ao ponto de reposição."
                >
                    <SettingsRow label="Estoque mínimo" htmlFor="item-minimo">
                        <input
                            id="item-minimo"
                            type="number"
                            min={0}
                            step="any"
                            className="input estab-field"
                            value={form.minStock}
                            onChange={(e) => handleChange('minStock', e.target.value)}
                        />
                    </SettingsRow>
                    <SettingsRow label="Ponto de reposição" htmlFor="item-rop">
                        <input
                            id="item-rop"
                            type="number"
                            min={0}
                            step="any"
                            className="input estab-field"
                            value={form.reorderPointManual}
                            onChange={(e) => handleChange('reorderPointManual', e.target.value)}
                        />
                    </SettingsRow>
                    <SettingsRow label="Prazo de entrega (dias)" htmlFor="item-prazo">
                        <input
                            id="item-prazo"
                            type="number"
                            min={0}
                            className="input estab-field"
                            value={form.leadTimeDays}
                            onChange={(e) => handleChange('leadTimeDays', e.target.value)}
                        />
                    </SettingsRow>
                </SettingsSection>

                <SettingsSection>
                    <SettingsRow label="Item ativo" htmlFor="item-ativo" inline>
                        <input
                            id="item-ativo"
                            type="checkbox"
                            className="toggle toggle-success"
                            checked={!!form.isActive}
                            onChange={(e) => handleChange('isActive', e.target.checked)}
                        />
                    </SettingsRow>
                </SettingsSection>

                <div className="estab-savebar gap-2">
                    <button
                        type="button"
                        className="btn btn-ghost flex-1 sm:flex-none sm:min-w-[8rem]"
                        onClick={onClose}
                        disabled={saving}
                    >
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        className="btn btn-primary flex-1 sm:flex-none sm:min-w-[11rem]"
                        disabled={saving}
                    >
                        {saving && <span className="loading loading-spinner loading-sm"></span>}
                        Salvar
                    </button>
                </div>
            </form>
        </Modal>
    );
}
